import PropTypes from 'prop-types';
import React from 'react';
// components
import { Stack, Typography, Divider, TextField, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import uploadFill from '@iconify/icons-eva/upload-fill';
import folderAddFill from '@iconify/icons-eva/folder-add-fill';
import eyeFill from '@iconify/icons-eva/eye-fill';
import trash2Outline from '@iconify/icons-eva/trash-2-outline';
import { useTheme, styled } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import toast from 'react-hot-toast';
import _ from 'lodash';
import { confirmAlert } from 'react-confirm-alert';
//
import * as attachmentService from '../../../services/attachment';
import { getAPIURL } from '../../../utils/envGetter';
import isEligible from '../../../utils/roleEligibleGetter';

const AddAttachment = ({ idRequirement, user, type }) => {
  const theme = useTheme();
  const Input = styled('input')({
    display: 'none'
  });
  const [attachment, setAttachment] = React.useState({
    title: '',
    description: '',
    file: '',
    filename: ''
  });

  const [attachments, setAttachments] = React.useState([]);
  const [isSubmitting, setSubmitting] = React.useState(false);
  const [toggleUpdate, setToggleUpdate] = React.useState(false);

  const getAttachments = async () => {
    if (!idRequirement) return;
    try {
      const result = await attachmentService.getAttachments(idRequirement);
      setAttachments(result.data);
      setAttachment({ title: '', description: '', file: '', filename: '' });
    } catch (error) {
      toast.error(error.message);
    }
  };

  React.useEffect(() => {
    getAttachments();
  }, [toggleUpdate]);

  function onFileChange(event) {
    if (event.target.files[0].size > 500024 * 10) {
      return toast.error(
        'Maaf, file Anda melebihi batas 5MB, harap compress dokumen terlebih dahulu'
      );
    }

    setAttachment({
      ...attachment,
      file: event.target.files[0],
      filename: event.target.files[0].name
    });
  }

  const uploadAttachment = async () => {
    if (!attachment.title) return toast.error('Maaf, judul lampiran belum diisi');
    if (!attachment.description) return toast.error('Maaf, deskripsi lampiran belum diisi');
    if (!attachment.file) return toast.error('Maaf, file tidak ditemukan');

    if (!idRequirement) return toast.error('Maaf, ID requirement tidak ditemukan');
    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('file', attachment.file);
      formData.append('upload_type', 'attachment');
      formData.append('title', attachment.title);
      formData.append('description', attachment.description);
      formData.append('creator', user?.attributes?.id);
      formData.append('id_requirement', idRequirement);

      toast
        .promise(attachmentService.addAttachment(formData), {
          loading: 'Mengunggah file ...',
          error: <b>Maaf, terjadi kesalahan</b>,
          success: <b>Dokumen berhasil diunggah</b>
        })
        .then((result) => {
          setToggleUpdate(!toggleUpdate);
        });
    } catch (error) {
      toast.error(error.message);
    }

    setSubmitting(false);
  };

  const openDocument = (item) => {
    const origin = getAPIURL();
    const path = `${origin}/pdf?path=${item.document_path.replace('.', '').substring(1)}&id=${
      item.id_requirement
    }&filename=${item.id_requirement}`;
    window.open(path, '_blank');
  };

  const deleteDocument = async (item) => {
    try {
      const { id } = item;

      confirmAlert({
        title: 'Konfirmasi',
        message: 'Apakah Anda yakin akan menghapus data ini?',
        buttons: [
          {
            label: 'Yakin',
            onClick: async () => {
              try {
                toast
                  .promise(attachmentService.deleteAttachment(id), {
                    loading: 'Menghapus file',
                    error: <b>Maaf, terjadi kesalahan</b>,
                    success: <b>Dokumen berhasil dihapus</b>
                  })
                  .then((result) => {
                    setToggleUpdate(!toggleUpdate);
                  });
              } catch (error) {
                toast.error(error.message);
              }
            }
          },
          {
            label: 'Tidak',
            onClick: () => console.log('No')
          }
        ]
      });
    } catch (error) {
      toast.error(error.message);
    }
  };
  return (
    <Stack direction="column" spacing={2}>
      <Typography variant="h5">Attachment</Typography>
      <Stack direction="column" spacing={2}>
        {idRequirement && isEligible(type) && (
          <Stack direction="column" spacing={2}>
            <Typography variant="body2">
              Silakan tambahkan file-file pendukung jika diperlukan (opsional)
            </Typography>
            <TextField
              label="Judul"
              value={attachment.title}
              onChange={(e) => setAttachment({ ...attachment, title: e.target.value })}
            />
            <TextField
              label="Deskripsi"
              value={attachment.description}
              onChange={(e) => setAttachment({ ...attachment, description: e.target.value })}
            />

            {!attachment.file && (
              <label htmlFor="uploader">
                <>{/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}</>
                <Input
                  accept="application/pdf"
                  id="uploader"
                  multiple
                  type="file"
                  onChange={(e) => onFileChange(e)}
                />
                <Button
                  component="span"
                  fullWidth
                  variant="outlined"
                  startIcon={<Icon icon={folderAddFill} />}
                  sx={{
                    color: theme.palette.warning.dark,
                    borderColor: theme.palette.warning.dark
                  }}
                >
                  Pilih file
                </Button>
              </label>
            )}
          </Stack>
        )}

        {!idRequirement && (
          <Stack direction="column" spacing={2}>
            <Typography variant="body2">
              Silakan simpan formulir terlebih dahulu untuk menggunakan fitur attachment
            </Typography>
          </Stack>
        )}

        {attachment.file && (
          <>
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              {attachment.filename}
            </Typography>
            <LoadingButton
              loading={isSubmitting}
              onClick={uploadAttachment}
              variant="outlined"
              startIcon={<Icon icon={uploadFill} />}
            >
              Upload file
            </LoadingButton>
          </>
        )}
      </Stack>

      {!_.isEmpty(attachments) && (
        <Stack direction="column" spacing={2}>
          <Typography variant="body2">List lampiran</Typography>
          <Stack direction="column" spacing={2}>
            {attachments.map((item, index) => (
              <Stack
                direction="row"
                spacing={2}
                key={item.id}
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={1} key={item.id} alignItems="center">
                  <Typography variant="body2">{index + 1}.</Typography>
                  <Typography variant="body2">{item.title}</Typography>
                </Stack>
                <Stack direction="row" spacing={1} key={item.id} alignItems="center">
                  <Icon
                    onClick={() => openDocument(item)}
                    cursor="pointer"
                    width={24}
                    height={24}
                    icon={eyeFill}
                    color={theme.palette.primary.main}
                  />
                  {isEligible(type) && (
                    <Icon
                      onClick={() => deleteDocument(item)}
                      cursor="pointer"
                      width={20}
                      height={20}
                      icon={trash2Outline}
                      color={theme.palette.error.main}
                    />
                  )}
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Stack>
      )}

      {_.isEmpty(attachments) && idRequirement && (
        <Typography variant="body2">Tidak ada data lampiran</Typography>
      )}

      <Divider />
    </Stack>
  );
};

AddAttachment.propTypes = {
  idRequirement: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired
};

export default AddAttachment;
